import * as Yup from 'yup';

import {
  CasLoaderWithBackdrop,
  CasReactHookFormDialog,
  CasRHFAutocompleteAsync,
  CasRHFDatePicker,
  CasRHFSwitch,
  CasRHFTextField,
} from 'src/compass/shared/components';
import useUpdateMutation from 'src/compass/shared/hooks/reactQuery/useUpdateMutation';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import { getBooleanSchema, getEmailSchema, getSelectSchema } from 'src/compass/shared/schemas';
import { useRetrieveDetailsById } from 'src/compass/shared/hooks/reactQuery/useRetrieveDetailsById';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';

interface Props {
  id: string;
  insuredName: string;
  onClose: VoidFunction;
  onUpdated: Function;
}

export const Schema = Yup.object().shape({
  claimsEmail: getEmailSchema('Claims Email').notRequired(),
  fwdContractEmail: getEmailSchema('Forward Contracts Email').notRequired(),
  sharepointFolderObj: getSelectSchema('Sharepoint Folder Name').notRequired(),
  disableFwdContractRequests: getBooleanSchema('Disable Fwd Contract Requests'),
  bfrStartDate: Yup.date().nullable(),
});

export default function GrowerDialog({ id, insuredName, onClose, onUpdated }: Props) {
  const { data, isFetching, isError } = useRetrieveDetailsById({
    entity: 'Sce/Grower',
    id,
  });

  const { mutate } = useUpdateMutation({
    url: 'Sce/Grower',
    onSuccess: (response) => {
      onSuccessEnqueueSnackbar(response);
      onUpdated();
      onClose();
    },
  });

  const onSubmit = (payload: any) => {
    const { sharepointFolderObj, bfrStartDate, ...rest } = payload;
    mutate({
      ...rest,
      sharepointFolderName: sharepointFolderObj?.name,
      bfrStartDate: bfrStartDate ? formatDate(bfrStartDate, DateFormat.YMD) : null,
    });
  };

  if (isFetching) return <CasLoaderWithBackdrop />;
  if (isError) throw new Error('Error retrieving data from the server.');

  const defaultValues = {
    ...data,
    sharepointFolderObj: data?.sharepointFolderName
      ? { id: data.sharepointFolderName, name: data.sharepointFolderName }
      : null,
  };

  return (
    <CasReactHookFormDialog
      open
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onClose={onClose}
      title={`${insuredName}: Settings`}
      schema={Schema}
    >
      <CasRHFTextField name="claimsEmail" label="Claims Email" />
      <CasRHFTextField name="fwdContractEmail" label="Forward Contracts Email" />
      <CasRHFAutocompleteAsync
        id={id}
        url="Sce/Grower/SelectSharepointFolderName/List"
        name="sharepointFolderObj"
        label="Sharepoint Folder Name"
      />
      <CasRHFSwitch name="disableFwdContractRequests" label="Disable Fwd Contract Requests" />
      <CasRHFDatePicker name="bfrStartDate" label="BFR Start Date" canClear />
    </CasReactHookFormDialog>
  );
}
